import { Link, useLocation } from 'react-router-dom';
import { Home, FileText, FileEdit, Settings, ChevronLeft, ChevronRight, Database } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store/useStore';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

export function Sidebar() {
  const { t } = useTranslation();
  const { isSidebarOpen, toggleSidebar, generatedDocuments, selectedDocIndex, setSelectedDocIndex, currentProjectId } = useStore();
  const location = useLocation();

  const isInProjectView = currentProjectId && (
    location.pathname.includes(`/iso/${currentProjectId}`) ||
    location.pathname.includes(`/dora/${currentProjectId}`) ||
    location.pathname.includes(`/projects/${currentProjectId}`) ||
    location.pathname.includes(`/iso-projects/${currentProjectId}`)
  );
  
  const navItems = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: Home },
    { path: '/repository', label: t('nav.repository'), icon: Database },
    { path: '/settings', label: t('nav.settings'), icon: Settings },
  ];

  return (
    <div className="relative flex-shrink-0 h-full">
      {/* Edge tab toggle — always visible, slides with the panel */}
      <button
        onClick={toggleSidebar}
        className={cn(
          'fixed top-1/2 -translate-y-1/2 z-40 flex items-center justify-center',
          'w-5 h-12 bg-primary text-primary-foreground rounded-r-lg shadow-md',
          'hover:bg-primary/90 transition-all duration-300',
          isSidebarOpen ? 'left-64' : 'left-0'
        )}
        aria-label={isSidebarOpen ? t('nav.collapseSidebar') : t('nav.expandSidebar')}
      >
        {isSidebarOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>

      {/* Sidebar panel */}
      <aside
        className={cn(
          'fixed left-0 top-0 h-full bg-card border-r border-border z-30 transition-transform duration-300 w-64',
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo/Header */}
        <div className="flex items-center p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <FileText className="h-6 w-6 text-primary" />
            <span className="font-brand font-bold text-xl tracking-tight">DocIntel</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center space-x-3 px-3 py-2 rounded-md transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon className="h-5 w-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Analyzed Documents Section */}
        {isInProjectView && generatedDocuments.length > 0 && (
          <div className="p-4 border-t border-border">
            <h3 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center">
              <FileEdit className="h-4 w-4 mr-2" />
              {t('nav.analyzedDocuments')}
            </h3>
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {generatedDocuments.map((doc: any, index: number) => (
                <button
                  key={doc.document_id || doc.generated_document_id || index}
                  onClick={() => {
                    setSelectedDocIndex(index);
                    if (doc.onSelect && typeof doc.onSelect === 'function') {
                      doc.onSelect();
                    }
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm rounded-md transition-colors',
                    index === selectedDocIndex
                      ? 'bg-accent text-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{doc.filename || doc.template_filename}</span>
                  </div>
                  {doc.summary?.total_changes && (
                    <div className="flex items-center gap-2 mt-1 text-xs">
                      <Badge variant="secondary" className="text-xs px-1 py-0">
                        {doc.summary.total_changes} {t('common.changes')}
                      </Badge>
                    </div>
                  )}
                  {doc.role && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {doc.role === 'filled' ? t('common.filled') : t('common.generated')}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
